import React from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "../../lib/utils";

export function PageHeader({ title, subtitle, actions, children, className }) {
  return (
    <div className={cn("flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 pb-4 border-b border-slate-100", className)}>
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight" style={{fontFamily: "\'Plus Jakarta Sans\', Inter, sans-serif"}}>{title}</h1>
        {subtitle ? <p className="text-sm text-slate-500 mt-1">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      {children}
    </div>
  );
}

export function SectionCard({ title, description, action, children, className, padding = "p-6" }) {
  return (
    <div className={cn("rounded-2xl border border-slate-200 bg-white shadow-sm", className)}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-slate-100">
          <div>
            {title ? <h3 className="text-base font-bold text-slate-900">{title}</h3> : null}
            {description ? <p className="text-xs text-slate-500 mt-0.5">{description}</p> : null}
          </div>
          {action}
        </div>
      )}
      <div className={padding}>{children}</div>
    </div>
  );
}

export function StatCard({ label, value, delta, icon: Icon, accent = "blue", helper }) {
  const accents = {
    blue:    "bg-blue-50 text-blue-600",
    violet:  "bg-violet-50 text-violet-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber:   "bg-amber-50 text-amber-600",
    rose:    "bg-rose-50 text-rose-600",
    slate:   "bg-slate-100 text-slate-600",
  };
  const positive = typeof delta === "string" ? delta.trim().startsWith("+") : (delta || 0) >= 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-extrabold text-slate-900 leading-tight">{value}</p>
        </div>
        {Icon && (
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", accents[accent] || accents.blue)}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
      {(delta || helper) && (
        <div className="mt-3 flex items-center gap-1.5 text-xs">
          {delta && (
            <span className={cn("inline-flex items-center gap-0.5 font-semibold", positive ? "text-emerald-600" : "text-rose-600")}>
              {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{delta}
            </span>
          )}
          {helper && <span className="text-slate-500">{helper}</span>}
        </div>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-10 text-center">
      {Icon && (
        <div className="mx-auto w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
          <Icon className="w-6 h-6" />
        </div>
      )}
      {title ? <h3 className="text-lg font-bold text-slate-900">{title}</h3> : null}
      {description ? <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">{description}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function ChipBadge({ tone = "blue", children, className }) {
  const tones = {
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold", tones[tone] || tones.slate, className)}>
      {children}
    </span>
  );
}
